/**
 * MagicBI Design System - FeatureCard Component
 * 
 * Specialized card component for displaying features with icons,
 * titles, descriptions, and optional highlights.
 */

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Card, CardBody } from '../Card/Card';
import { Heading, Text } from '../../primitives/Typography/Typography';
import { Box } from '../../primitives/Layout/Layout';

// FeatureCard props interface
export interface FeatureCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Feature icon */
  icon: React.ReactNode;
  /** Feature title */
  title: string;
  /** Feature description */
  description: string;
  /** Whether feature is highlighted */
  highlighted?: boolean;
  /** Whether card is interactive */
  interactive?: boolean;
  /** Custom icon color */
  iconColor?: string;
  /** Custom icon background */
  iconBackground?: string;
  /** Card variant */
  variant?: 'default' | 'elevated' | 'outlined';
  /** Card size */
  size?: 'sm' | 'md' | 'lg';
}

// Icon container
const IconContainer = styled.div<{
  $highlighted?: boolean;
  $iconColor?: string;
  $iconBackground?: string;
}>`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.semantic.inset.md};
  
  ${({ $highlighted, $iconBackground, theme }) => {
    if ($iconBackground) {
      return css`
        background: ${$iconBackground};
      `;
    }
    
    if ($highlighted) {
      return css`
        background: ${theme.colors.background.gradientPrimary};
      `;
    }
    
    return css`
      background: ${theme.colors.background.gradientSecondary};
    `;
  }}
  
  color: ${({ $iconColor, theme }) => $iconColor || theme.colors.text.inverse};
  
  svg {
    width: 32px;
    height: 32px;
  }
`;

// Feature title
const FeatureTitle = styled(Heading)`
  margin-bottom: ${({ theme }) => theme.spacing.semantic.inset.sm};
`;

// Feature description
const FeatureDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`;

// Styled feature card
const StyledFeatureCard = styled(Card)<{
  $highlighted?: boolean;
  $interactive?: boolean;
}>`
  text-align: center;
  transition: ${({ theme }) => theme.transitions.semantic.interactive.button.all};
  
  ${({ $highlighted, theme }) => $highlighted && css`
    border-color: ${theme.colors.interactive.primary};
    box-shadow: ${theme.shadows.interactive.focus}, ${theme.shadows.component.card.default};
  `}
  
  ${({ $interactive }) => $interactive && css`
    cursor: pointer;
    
    &:hover {
      transform: translateY(-4px);
      
      ${IconContainer} {
        transform: scale(1.05);
      }
    }
  `}
`;

// FeatureCard component
export const FeatureCard = forwardRef<HTMLDivElement, FeatureCardProps>(
  (
    {
      icon,
      title,
      description,
      highlighted = false,
      interactive = false,
      iconColor,
      iconBackground,
      variant = 'default',
      size = 'md',
      ...props
    },
    ref
  ) => {
    return (
      <StyledFeatureCard
        ref={ref}
        variant={variant}
        size={size}
        $highlighted={highlighted}
        $interactive={interactive}
        interactive={interactive}
        {...props}
      >
        <CardBody>
          <IconContainer
            $highlighted={highlighted}
            $iconColor={iconColor}
            $iconBackground={iconBackground}
          >
            {icon}
          </IconContainer>
          
          <FeatureTitle as="h3" level={3}>
            {title}
          </FeatureTitle>
          
          <FeatureDescription>
            {description}
          </FeatureDescription>
        </CardBody>
      </StyledFeatureCard>
    );
  }
);

FeatureCard.displayName = 'FeatureCard';

export default FeatureCard;
